// @flow
import moment from 'moment';
import {List, Map} from 'immutable';
import {Store} from 'client/framework';
import {getStudentIntervalStatsForTeacher} from 'framework/MandarkAPI/Students/Students';
import {isResourcePopulated} from 'resources/mandark.resource';
import {mandarkEndpoint} from '@albert-io/json-api-framework/request/builder/legacy';
import appStore from 'client/AppStore';
import {stats} from 'lib/StatsUtil';

import teacherClassroomScopeStore from '../../TeacherClassroomScope/TeacherClassroomScope.store';

class StudentOverviewStore extends Store {
  getInterval(): string {
    // These values come from our date range input, so we moment-ize them again
    const start = moment(this._getStartDate());
    const end = moment(this._getEndDate());

    // TODO: Decide if this is something worth having in a util or not
    const days = end.diff(start, 'days');
    if (days < 14) {
      return 'day';
    }
    if (days >= 14 && days <= 98) {
      return 'week';
    }
    return 'month';
  }

  _getStartDate(): number {
    return teacherClassroomScopeStore.getStartDate();
  }

  _getEndDate(): number {
    return teacherClassroomScopeStore.getEndDate();
  }

  getAttemptNumber(): ?number {
    return teacherClassroomScopeStore.getAttemptNumber();
  }

  getSubject(): List<*> {
    return teacherClassroomScopeStore.getSelectedSubjects();
  }

  makeClassroomQuery(): Object {
    return mandarkEndpoint(['classrooms_v1', appStore.routerProps.params.classId])
      .include('subjects_v1')
      .done();
  }

  hasClassroomData(): boolean {
    return isResourcePopulated(this.makeClassroomQuery());
  }

  makeStudentTimeSeriesQuery(): Object {
    const attemptNumber = this.getAttemptNumber(); // defaults to 1 in the scope store.
    const isMostRecent = teacherClassroomScopeStore.isMostRecentAttempt();

    const query: {
      interval: string,
      start_date: number,
      end_date: number,
      most_recent?: boolean,
      attempt_number?: ?number,
      subjects?: string
    } = {
      interval: this.getInterval(),
      start_date: this._getStartDate(),
      end_date: this._getEndDate(),
      subjects: this.getSubject().join(',')
    };

    if (isMostRecent) {
      query.most_recent = isMostRecent;
    } else {
      query.attempt_number = attemptNumber;
    }

    return query;
  }

  hasStudentTimeSeriesData(): boolean {
    // Date range store presence required for query.
    // classroom subjects also required for our time series query
    return this.hasClassroomData() && this._getStudentTimeSeriesData().hasReceivedData();
  }

  _getStudentTimeSeriesDataInterface(): Object {
    return this._getStudentTimeSeriesData().interface;
  }

  _getStudentTimeSeriesData(): Object {
    return getStudentIntervalStatsForTeacher({
      query: this.makeStudentTimeSeriesQuery(),
      studentId: appStore.routerProps.params.userId
    });
  }

  /**
   * Generates a map containg summary data for the time series graphs we'll be showing
   *
   * This is done first by doign a reduce to aggregation/sum things,
   * and then some final math is done to translate things into a map used in our
   * statistical summary section.
   *
   * The keys in this map are camelcased analogues of what is returned from the endpoint for each interval
   *
   *
   * This method returns a suite of functions for accessing stats in the generated map.
   */
  getIntervalDataSummary(): Object {
    let intervalDataSummary = this._getStudentTimeSeriesDataInterface()
      .getIntervalData()
      .reduce((acc, interval) => {
        const totalAnswered = acc.get('totalQuestionsAnswered', 0);
        const pointsEarned = acc.get('sumOfPointsEarned', 0);
        const milliseconds = acc.get('timeSpentGuessingDuration', moment.duration(0));
        const countOfCorrectGuesses = acc.get('countOfCorrectGuesses', 0);
        const countOfGuesses = acc.get('countOfGuesses', 0);

        const intervalCorrectGuesses = Math.round(
          interval.get('count_of_guesses') * interval.get('average_accuracy')
        );

        const intervalMillisecondsSpentGuessing = parseInt(
          interval.get('sum_of_milliseconds_spent_guessing'),
          10
        );
        if (Number.isNaN(intervalMillisecondsSpentGuessing)) {
          throw new Error('Unable to read time spent guessing from server');
        }

        return acc
          .set(
            'totalQuestionsAnswered',
            totalAnswered + interval.get('count_of_questions_answered')
          )
          .set('sumOfPointsEarned', pointsEarned + interval.get('sum_of_points_earned'))
          .set('countOfCorrectGuesses', countOfCorrectGuesses + intervalCorrectGuesses)
          .set('countOfGuesses', countOfGuesses + interval.get('count_of_guesses'))
          .set(
            'timeSpentGuessingDuration',
            milliseconds.add(moment.duration(intervalMillisecondsSpentGuessing))
          );
      }, Map());

    const accuracy = stats.percentage(
      intervalDataSummary.get('countOfCorrectGuesses'),
      intervalDataSummary.get('countOfGuesses')
    );

    intervalDataSummary = intervalDataSummary.set('averageAccuracy', accuracy);

    return {
      getTotalQuestionsAnswered: () => intervalDataSummary.get('totalQuestionsAnswered'),
      getSumOfPointsEarned: () => intervalDataSummary.get('sumOfPointsEarned'),
      getAverageAccuracy: () => intervalDataSummary.get('averageAccuracy'),
      getTimeSpentGuessing: () => {
        /*
          Because moment will expose on a duration .asHours, .asMinutes, etc - we can't use those directly
          on the return here because they represent the entire range.

          We grab the milliseconds and compute it ourselves
        */
        const milliseconds = intervalDataSummary.get('timeSpentGuessingDuration').asMilliseconds();
        const seconds = milliseconds / 1000;
        const minutes = Math.round(seconds / 60);

        // What we will interpolate
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes - hours * 60;

        return `${hours} hrs ${remainingMinutes} min`;
      }
    };
  }

  /**
   * Internally, generates a map of lists containing data points for our
   * corresponding graphs.
   *
   * We return a suite of getter functions to the front end that will read the appropraite lists from the
   * map we are generating.
   */
  getIntervalChartData(): Object {
    function makeDataPoint(date, val): Map<string, *> {
      return new Map({
        date,
        count: val
      });
    }

    const intervalDataPoints = this._getStudentTimeSeriesDataInterface()
      .getIntervalData()
      .reduce((acc, interval) => {
        const questionsAnsweredData = acc.get('questionsAnsweredData', new List());
        const accuracyData = acc.get('accuracyData', new List());
        const timeSpentData = acc.get('timeSpentData', new List());

        const millisecondsSpentGuessing = parseInt(
          interval.get('sum_of_milliseconds_spent_guessing'),
          10
        );
        if (Number.isNaN(millisecondsSpentGuessing)) {
          throw new Error('Unable to read time spent guessing from server');
        }
        const momentMillisecondDuration = moment.duration(millisecondsSpentGuessing);

        return (
          acc
            .set(
              'questionsAnsweredData',
              questionsAnsweredData.push(
                makeDataPoint(moment(interval.get('inserted_at')), interval.get('count_of_guesses'))
              )
            )
            // again, this is represented to the right of the decimal point
            .set(
              'accuracyData',
              accuracyData.push(
                makeDataPoint(
                  moment(interval.get('inserted_at')),
                  (interval.get('average_accuracy') * 100).toFixed(2)
                )
              )
            )
            // We provide the graph with hours. We translate miliseconds to seconds, to minutes, then to hours.
            .set(
              'timeSpentData',
              timeSpentData.push(
                makeDataPoint(
                  moment(interval.get('inserted_at')),
                  momentMillisecondDuration.asHours().toFixed(2)
                )
              )
            )
        );
      }, new Map());

    return {
      getQuestionsAnsweredData: () => intervalDataPoints.get('questionsAnsweredData'),
      getAccuracyData: () => intervalDataPoints.get('accuracyData'),
      getTimeSpentData: () => intervalDataPoints.get('timeSpentData')
    };
  }
}

export default new StudentOverviewStore('StudentOverviewStore');
